import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AppCtx = createContext(null);

const LS = {
  cart: "sa_cart",
  wishlist: "sa_wishlist",
  recent: "sa_recent",
};

const readLS = (k, fallback) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
};
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

export function AppProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [cart, setCart] = useState(() => readLS(LS.cart, []));
  const [wishlist, setWishlist] = useState(() => readLS(LS.wishlist, []));
  const [recent, setRecent] = useState(() => readLS(LS.recent, []));
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => { writeLS(LS.cart, cart); }, [cart]);
  useEffect(() => { writeLS(LS.wishlist, wishlist); }, [wishlist]);
  useEffect(() => { writeLS(LS.recent, recent); }, [recent]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/categories").then((r) => {
      setCategories(r.data.categories || []);
      setCollections(r.data.collections || []);
    }).catch(() => {});
  }, []);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        image: product.images?.[0] || null,
        quantity: qty,
      }];
    });
    toast.success(`${product.name} added to cart`);
    setCartOpen(true);
  }, []);

  const updateQty = useCallback((product_id, qty) => {
    setCart((prev) => prev.map((i) => i.product_id === product_id ? { ...i, quantity: Math.max(1, qty) } : i));
  }, []);

  const removeFromCart = useCallback((product_id) => {
    setCart((prev) => prev.filter((i) => i.product_id !== product_id));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) {
        toast.message(`Removed from wishlist`);
        return prev.filter((p) => p.id !== product.id);
      }
      toast.success(`Added to wishlist`);
      return [{ id: product.id, name: product.name, slug: product.slug, price: product.price, mrp: product.mrp, image: product.images?.[0] }, ...prev];
    });
  }, []);

  const inWishlist = useCallback((id) => wishlist.some((p) => p.id === id), [wishlist]);

  const pushRecent = useCallback((product) => {
    setRecent((prev) => {
      const filtered = prev.filter((p) => p.id !== product.id);
      const next = [{ id: product.id, name: product.name, slug: product.slug, price: product.price, mrp: product.mrp, image: product.images?.[0] }, ...filtered];
      return next.slice(0, 12);
    });
  }, []);

  const cartCount = useMemo(() => cart.reduce((a, i) => a + i.quantity, 0), [cart]);
  const cartSubtotal = useMemo(() => cart.reduce((a, i) => a + i.quantity * i.price, 0), [cart]);

  const value = {
    settings, categories, collections,
    cart, cartCount, cartSubtotal,
    cartOpen, setCartOpen,
    searchOpen, setSearchOpen,
    addToCart, updateQty, removeFromCart, clearCart,
    wishlist, toggleWishlist, inWishlist,
    recent, pushRecent,
    refreshSettings: () => api.get("/settings").then((r) => setSettings(r.data)),
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp() {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
